<template>
  <div id="app" class="flex h-screen bg-gray-50" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <!-- Sidebar Navigation -->
    <Sidebar @collapse-change="handleSidebarCollapse" />
    
    <!-- Main Content Area -->
    <main 
      class="flex-1 overflow-auto transition-all duration-150"
      :class="sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'"
    >
      <div class="min-h-screen gradient-bg flex flex-col">
        <!-- Page Content -->
        <div class="flex-1">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <router-view />
          </div>
        </div>

        <!-- Footer -->
        <footer class="mt-auto border-t border-gray-200/60 bg-white/50 backdrop-blur-sm">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div class="flex flex-col md:flex-row justify-between items-center">
              <div class="text-sm text-gray-600">
                © 2024 {{ appName }}. Built with Vue 3 & NestJS.
              </div>
              <div class="flex space-x-4 mt-4 md:mt-0">
                <a href="#" class="text-sm text-gray-500 hover:text-gray-700 transition-colors">Documentation</a>
                <a href="#" class="text-sm text-gray-500 hover:text-gray-700 transition-colors">API Reference</a>
                <a href="/health" class="text-sm text-gray-500 hover:text-gray-700 transition-colors">Status</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </main>

    <!-- Toast Notifications -->
    <ToastNotification />

    <!-- Confirm Dialog -->
    <ConfirmDialog
      :show="confirmState.show"
      :title="confirmState.title"
      :message="confirmState.message"
      :confirm-text="confirmState.confirmText"
      :cancel-text="confirmState.cancelText"
      :type="confirmState.type"
      :loading="confirmState.loading"
      @confirm="handleConfirmDialogConfirm"
      @cancel="handleConfirmDialogCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, provide } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { useToast } from '@/utils/toast';
import { useConfirm } from '@/utils/confirm';

const appName = computed(() => 
  import.meta.env.VITE_APP_NAME || 'Flow'
);

// Track sidebar collapsed state
const sidebarCollapsed = ref(false);

// Handle sidebar collapse state changes
const handleSidebarCollapse = (collapsed: boolean) => {
  sidebarCollapsed.value = collapsed;
};

// Provide toast state to all child components
const toastState = useToast();
provide('toastState', toastState);

// Setup confirm dialog
const { confirmState, handleConfirm, handleCancel } = useConfirm();

const handleConfirmDialogConfirm = () => {
  handleConfirm();
};

const handleConfirmDialogCancel = () => {
  handleCancel();
};
</script>
